import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { OrganizationsService } from '../../../../services/organizations/organizations.service.js';
import { MatIconModule } from "@angular/material/icon";
import { MatButtonModule } from "@angular/material/button";
import { MatTabsModule } from '@angular/material/tabs';
import { provideNativeDateAdapter } from '@angular/material/core';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { KeyValuePair } from 'allai-admin-shared';
import { ActivatedRoute, Router } from '@angular/router';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from "@angular/forms";
import { MatFormFieldModule } from "@angular/material/form-field";
import { SnackbarService } from "../../../../services/snackbar.service";
import { PageLoaderService } from "../../../../services/page-loader.service";
import { MatRadioModule } from "@angular/material/radio";
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatInputModule } from "@angular/material/input";
import { firstValueFrom } from "rxjs";
import { HttpClient } from "@angular/common/http";
import { jsonValidator } from "allai-admin-shared";
import { Organization } from '@allaihealth/util';
import { environment } from "../../../../../environments/environment";

/** The organization detail page */
@Component({
	selector: 'app-organization-detail',
	imports: [
		MatIconModule,
		MatButtonModule,
		MatTabsModule,
		MatTableModule,
		ReactiveFormsModule,
		FormsModule,
		MatFormFieldModule,
		MatRadioModule,
		MatCheckboxModule,
		MatInputModule
	],
	providers: [provideNativeDateAdapter()],
	templateUrl: './organization-detail.component.html',
	styleUrl: './organization-detail.component.scss'
})

export class OrganizationDetailComponent implements OnInit, OnDestroy {

	/** Interaction with backend */
	protected http = inject(HttpClient);

	/** Organization form */
	form = new FormGroup({
		id: new FormControl(''),
		name: new FormControl('', Validators.required),
		enabled: new FormControl(false),
		trialAccount: new FormControl(false),
		surveyCategories: new FormControl(''),
		aiEnabled: new FormControl(false),
		aiSettings: new FormControl(''),
		daysFromAppt: new FormControl(1, Validators.required),
		timeZone: new FormControl('', Validators.required),
		communicationEnabled: new FormControl(false)
	});

	organizationDataSource: MatTableDataSource<KeyValuePair>;
	readonly displayedColumns: (keyof KeyValuePair)[] = ['key', 'value'];

	constructor(private organizationsService: OrganizationsService,
							private route: ActivatedRoute,
							private loaderService: PageLoaderService,
							private snackbar: SnackbarService,) {
	}

	async ngOnInit() {
		this.organizationsService.onNewOrganizationDetail.subscribe(() => {
			this.refresh();
		});

		this.loaderService.show("Loading Organization...");
		await this.organizationsService.resolveOrganizationDetail(this.route.snapshot.params["id"]);
		this.loaderService.close();
	}
	private refresh() {

		let organizationDetail = this.organizationsService.organizationDetail;

		let data: KeyValuePair[] = [];
		if(organizationDetail) {

			let survey_categories = (organizationDetail.organization as any).survey_categories;
			if (survey_categories) {
				survey_categories = JSON.stringify(survey_categories);
			}
			let ai_settings = (organizationDetail.organization as any).ai_settings;
			if (ai_settings) {
				ai_settings = JSON.stringify(ai_settings);
			} else {
				ai_settings = '';
			}

			this.form.get('id').setValue(organizationDetail.organization.id);
			this.form.get('name').setValue(organizationDetail.organization.name);
			this.form.get('enabled').setValue(organizationDetail.organization.enabled);
			this.form.get('trialAccount').setValue((organizationDetail.organization as any).trial_account);
			this.form.get('aiEnabled').setValue(organizationDetail.organization.ai_hpi_enabled);
			this.form.get('surveyCategories').setValue(survey_categories);
			this.form.get('aiSettings').setValue(ai_settings);
			this.form.get('daysFromAppt').setValue((organizationDetail.organization as any).days_from_appointment);
			this.form.get('timeZone').setValue((organizationDetail.organization as any).time_zone);
		}
	}

	ngOnDestroy() {
	}

	/** Submit the update request */
	async submit() {
		if (this.form.valid) {
			try {

				this.loaderService.show("Updating Organization...");

				let ai_settings = this.form.value.aiSettings;
				if (this.form.value.aiSettings == '') {
					ai_settings = null;
				}

				let organization:Organization = new Organization();
				organization.id = this.form.value.id;
				organization.name = this.form.value.name;
				organization.enabled = this.form.value.enabled;
				organization.trial_account = this.form.value.trialAccount;
				organization.ai_hpi_enabled = this.form.value.aiEnabled;
				organization.ai_settings = ai_settings;
				organization.survey_categories = this.form.value.surveyCategories;
				organization.days_from_appointment = this.form.value.daysFromAppt;
				organization.time_zone = this.form.value.timeZone;

				// update the Organization
				const result = await firstValueFrom(this.http.put(`${environment.apiUrl}/organizations/${organization.id}/update`, organization));

				this.snackbar.error("Organization saved successfully.");

			} catch (e) {
				console.warn("Failed to update Organization", e);
				this.snackbar.error("Failed to update Organization");
			} finally {
				this.loaderService.close();
			}
		} else {
			this.snackbar.error("Unable to save, errors found");
		}
	}
}
